import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, ArrowDownToLine, FileSpreadsheet, Coins, Plus, Trash2, Settings, TrendingUp, TrendingDown, Wallet, Building2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import CashKassirsTab from './CashKassirsTab';
import BuildingsTab from './BuildingsTab';
import InvoicesTab from './InvoicesTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/accounting`;

const EVENT_TYPE_LABEL = {
  bank_received: 'Bank köçürmə',
  billing_total: 'Billing yığımı',
  commission_paid: 'Komissiya ödəniş',
};

const EVENT_TYPE_COLOR = {
  bank_received: 'bg-blue-50 text-blue-700 border-blue-200',
  billing_total: 'bg-purple-50 text-purple-700 border-purple-200',
  commission_paid: 'bg-orange-50 text-orange-700 border-orange-200',
};

function formatMoney(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₼';
}

export default function AccountingPanel({ user }) {
  const [tab, setTab] = useState('terminals'); // terminals | config (later: cash, buildings, invoices)
  const [terminals, setTerminals] = useState([]);
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Event creation modal state
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    terminal_id: '',
    event_type: 'bank_received',
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    period_month: new Date().toISOString().slice(0, 7),
    reference: '',
    note: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, eRes] = await Promise.all([
        axios.get(`${API}/terminals`, { headers }),
        axios.get(`${API}/config`, { headers }),
        axios.get(`${API}/terminal-events`, { headers }),
      ]);
      setTerminals(tRes.data || []);
      setConfig(cRes.data || null);
      setEvents(eRes.data || []);
    } catch (e) {
      toast.error('Məlumatlar yüklənmədi: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEventModal = (terminal_id, event_type) => {
    setEventForm({
      terminal_id,
      event_type,
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      period_month: new Date().toISOString().slice(0, 7),
      reference: '',
      note: '',
    });
    setEventModalOpen(true);
  };

  const submitEvent = async () => {
    const amount = parseFloat(eventForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Məbləğ müsbət olmalıdır');
      return;
    }
    try {
      await axios.post(`${API}/terminal-events`, {
        ...eventForm,
        amount,
      }, { headers });
      toast.success('Qeyd əlavə edildi');
      setEventModalOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error('Xəta: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Bu qeydi silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/terminal-events/${id}`, { headers });
      toast.success('Silindi');
      await fetchAll();
    } catch (e) {
      toast.error('Silinmədi: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ====== UI ======
  if (loading) {
    return <div className="p-8 text-center text-gray-500">Yüklənir...</div>;
  }

  return (
    <div className="space-y-4" data-testid="accounting-panel">
      {/* Sub tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-2">
        <Button
          variant={tab === 'terminals' ? 'default' : 'outline'}
          onClick={() => setTab('terminals')}
          className={tab === 'terminals' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          data-testid="acc-tab-terminals"
        >
          <Banknote className="w-4 h-4 mr-1" /> Terminallar
        </Button>
        <Button
          variant={tab === 'cash' ? 'default' : 'outline'}
          onClick={() => setTab('cash')}
          className={tab === 'cash' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          data-testid="acc-tab-cash"
        >
          <Wallet className="w-4 h-4 mr-1" /> Nağd & Kassirlər
        </Button>
        <Button
          variant={tab === 'buildings' ? 'default' : 'outline'}
          onClick={() => setTab('buildings')}
          className={tab === 'buildings' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          data-testid="acc-tab-buildings"
        >
          <Building2 className="w-4 h-4 mr-1" /> Binalar
        </Button>
        <Button
          variant={tab === 'invoices' ? 'default' : 'outline'}
          onClick={() => setTab('invoices')}
          className={tab === 'invoices' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          data-testid="acc-tab-invoices"
        >
          <FileText className="w-4 h-4 mr-1" /> Qaimələr
        </Button>
        <Button
          variant={tab === 'config' ? 'default' : 'outline'}
          onClick={() => setTab('config')}
          data-testid="acc-tab-config"
        >
          <Settings className="w-4 h-4 mr-1" /> Konfiqurasiya
        </Button>
      </div>

      {tab === 'terminals' && (
        <TerminalsTab
          terminals={terminals}
          events={events}
          openEventModal={openEventModal}
          deleteEvent={deleteEvent}
        />
      )}

      {tab === 'cash' && <CashKassirsTab />}
      {tab === 'buildings' && <BuildingsTab />}
      {tab === 'invoices' && <InvoicesTab />}

      {tab === 'config' && config && (
        <ConfigTab config={config} isSuperAdmin={user?.is_superadmin} onUpdated={fetchAll} />
      )}

      {/* Event create modal */}
      <Dialog open={eventModalOpen} onOpenChange={setEventModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Terminal qeydi əlavə et</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Terminal</label>
              <Select
                value={eventForm.terminal_id}
                onValueChange={(v) => setEventForm({ ...eventForm, terminal_id: v })}
              >
                <SelectTrigger data-testid="event-terminal-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {terminals.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Hadisə tipi</label>
              <Select
                value={eventForm.event_type}
                onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}
              >
                <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_received">Bank köçürmə (terminaldan banka)</SelectItem>
                  <SelectItem value="billing_total">Ay sonu Billing yığımı</SelectItem>
                  <SelectItem value="commission_paid">Komissiya ödənişi (bizdən terminala)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Tarix</label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Məbləğ (₼)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={eventForm.amount}
                  onChange={(e) => setEventForm({ ...eventForm, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="event-amount-input"
                />
              </div>
            </div>
            {eventForm.event_type === 'billing_total' && (
              <div>
                <label className="text-sm font-medium">Hesabat ayı (YYYY-MM)</label>
                <Input
                  type="month"
                  value={eventForm.period_month}
                  onChange={(e) => setEventForm({ ...eventForm, period_month: e.target.value })}
                  data-testid="event-period-input"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Referans (istəyə bağlı)</label>
              <Input
                value={eventForm.reference}
                onChange={(e) => setEventForm({ ...eventForm, reference: e.target.value })}
                placeholder="Bank referans №, qeyd"
                data-testid="event-reference-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Qeyd</label>
              <Input
                value={eventForm.note}
                onChange={(e) => setEventForm({ ...eventForm, note: e.target.value })}
                data-testid="event-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventModalOpen(false)}>Ləğv et</Button>
            <Button onClick={submitEvent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="event-submit-btn">
              Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TerminalsTab({ terminals, events, openEventModal, deleteEvent }) {
  return (
    <div className="space-y-6">
      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {terminals.map(t => {
          const balancePositive = t.balance >= 0;
          return (
            <div key={t.id} className="bg-white rounded-xl border shadow-sm p-4 flex flex-col" data-testid={`terminal-card-${t.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-800">{t.name}</h3>
                </div>
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{t.commission_rate}%</span>
              </div>

              <div className="space-y-1.5 text-sm">
                <Row label="Cum. Billing yığımı" value={formatMoney(t.cum_billing)} />
                <Row label="Cum. Banka köçürülən" value={formatMoney(t.cum_bank_received)} />
                <div className="border-t my-2"></div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Terminal balansı:</span>
                  <span className={`font-bold ${balancePositive ? 'text-emerald-700' : 'text-red-700'}`}>
                    {balancePositive ? '+' : ''}{formatMoney(t.balance)}
                  </span>
                </div>
                <p className={`text-xs ${balancePositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {balancePositive
                    ? (t.balance > 0 ? '✅ Terminal artıq ödəyib' : '✅ Tam hesablaşma')
                    : '⚠️ Terminal bizə borcludur'}
                </p>
                <div className="border-t my-2"></div>
                <Row label="Hesablanmış komissiya (2.5%)" value={formatMoney(t.cum_bank_received * t.commission_rate / 100)} />
                <Row label="Ödənilib" value={formatMoney(t.cum_commission_paid)} />
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Qalıq komissiya borcumuz:</span>
                  <span className="font-bold text-orange-600">
                    {formatMoney(t.commission_owed)}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                <Button size="sm" onClick={() => openEventModal(t.id, 'bank_received')} className="bg-blue-600 hover:bg-blue-700" data-testid={`add-bank-${t.id}`}>
                  <ArrowDownToLine className="w-3 h-3 mr-1" /> Bank köçürmə əlavə et
                </Button>
                <Button size="sm" onClick={() => openEventModal(t.id, 'billing_total')} variant="outline" data-testid={`add-billing-${t.id}`}>
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Ay sonu Billing daxil et
                </Button>
                <Button size="sm" onClick={() => openEventModal(t.id, 'commission_paid')} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`add-commission-${t.id}`}>
                  <Coins className="w-3 h-3 mr-1" /> Komissiya ödə
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Events history */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Tarixçə ({events.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Tarix</th>
                <th className="text-left px-3 py-2">Terminal</th>
                <th className="text-left px-3 py-2">Tip</th>
                <th className="text-right px-3 py-2">Məbləğ</th>
                <th className="text-left px-3 py-2">Dövr/Ref</th>
                <th className="text-left px-3 py-2">İstifadəçi</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan="7" className="text-center py-6 text-gray-400">Hələ qeyd yoxdur</td></tr>
              )}
              {events.map(e => (
                <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`event-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                  <td className="px-3 py-2">{e.terminal_id}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border ${EVENT_TYPE_COLOR[e.event_type]}`}>
                      {EVENT_TYPE_LABEL[e.event_type]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{formatMoney(e.amount)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {e.period_month || e.reference || '-'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{e.created_by_username}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => deleteEvent(e.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-event-${e.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-600">{label}:</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}

function ConfigTab({ config, isSuperAdmin, onUpdated }) {
  const [form, setForm] = useState({
    tax_rate: config.tax_rate,
    vat_rate: config.vat_rate,
    terminal_commission_rate: config.terminal_commission_rate,
    cash_deduction_rate: config.cash_deduction_rate,
  });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config`, {
        tax_rate: parseFloat(form.tax_rate),
        vat_rate: parseFloat(form.vat_rate),
        terminal_commission_rate: parseFloat(form.terminal_commission_rate),
        cash_deduction_rate: parseFloat(form.cash_deduction_rate),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Konfiqurasiya yeniləndi');
      onUpdated && onUpdated();
    } catch (e) {
      toast.error('Xəta: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm p-6 max-w-2xl">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5" /> Mühasibatlıq Konfiqurasiyası
      </h3>
      <div className="space-y-4">
        <ConfigRow
          label="Cəmi vergi/komissiya tutumu (bina) — 18% ƏDV + 2.5% terminal + 0.5% bank"
          value={form.tax_rate}
          onChange={(v) => setForm({ ...form, tax_rate: v })}
          disabled={!isSuperAdmin}
          suffix="%"
          testId="cfg-tax-rate"
        />
        <ConfigRow
          label="ƏDV faizi (qaimələr)"
          value={form.vat_rate}
          onChange={(v) => setForm({ ...form, vat_rate: v })}
          disabled={!isSuperAdmin}
          suffix="%"
          testId="cfg-vat-rate"
        />
        <ConfigRow
          label="Terminal komissiya faizi"
          value={form.terminal_commission_rate}
          onChange={(v) => setForm({ ...form, terminal_commission_rate: v })}
          disabled={!isSuperAdmin}
          suffix="%"
          testId="cfg-terminal-commission"
        />
        <ConfigRow
          label="Bina nağd ödəniş əlavə tutumu"
          value={form.cash_deduction_rate}
          onChange={(v) => setForm({ ...form, cash_deduction_rate: v })}
          disabled={!isSuperAdmin}
          suffix="%"
          testId="cfg-cash-deduction"
        />
      </div>
      {isSuperAdmin ? (
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cfg-save-btn">
            {saving ? 'Saxlanır...' : 'Yadda saxla'}
          </Button>
        </div>
      ) : (
        <p className="mt-6 text-xs text-gray-500">⚠️ Yalnız SuperAdmin dəyişdirə bilər</p>
      )}
    </div>
  );
}

function ConfigRow({ label, value, onChange, disabled, suffix, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-700 flex-1">{label}</span>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-24 text-right"
          data-testid={testId}
        />
        <span className="text-sm text-gray-500">{suffix}</span>
      </div>
    </div>
  );
}
